import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Crown, 
  Trophy, 
  Zap, 
  Calendar, 
  MapPin, 
  Globe, 
  Twitter, 
  Linkedin,
  MessageSquare,
  UserPlus,
  UserMinus,
  Shield,
  ShieldOff,
  Clock,
  Check,
  X,
  Award,
  TrendingUp,
  Target,
  Users,
  Mic,
  BookOpen
} from 'lucide-react';
import api from '../services/api';
import { useUser } from '../context/UserContext';

const UserProfile = () => {
  const { userId } = useParams();
  const { user: currentUser } = useUser();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    loadUserProfile();
  }, [userId]);

  const loadUserProfile = async () => {
    setLoading(true);
    try {
      const response = await api.getUserProfile(userId);
      setUser(response.data);
    } catch (error) {
      console.error('Error loading user profile:', error);
      setMessage('Failed to load user profile');
    } finally {
      setLoading(false);
    }
  };

  const handleFriendAction = async (action) => {
    try {
      switch (action) {
        case 'send':
          await api.sendFriendRequest(userId);
          setMessage('Friend request sent!');
          break;
        case 'accept':
          await api.acceptFriendRequest(userId);
          setMessage('Friend request accepted!');
          break;
        case 'decline':
          await api.declineFriendRequest(userId);
          setMessage('Friend request declined');
          break;
        case 'cancel':
          await api.cancelFriendRequest(userId);
          setMessage('Friend request cancelled');
          break;
        case 'remove':
          if (window.confirm('Are you sure you want to remove this friend?')) {
            await api.removeFriend(userId);
            setMessage('Friend removed');
          }
          break;
        case 'block':
          if (window.confirm('Are you sure you want to block this user?')) {
            await api.blockUser(userId);
            setMessage('User blocked');
          }
          break;
        case 'unblock':
          await api.unblockUser(userId);
          setMessage('User unblocked');
          break;
        case 'chat':
          // Navigate to chat with this user
          window.location.href = `/chat/with/${userId}`;
          break;
        case 'report':
          // Navigate to report user page
          window.location.href = `/report-user/${userId}`;
          break;
      }
      loadUserProfile(); // Reload to update friendship status
    } catch (error) {
      console.error('Friend action error:', error);
      setMessage(error.message || 'Action failed');
    }
  };

  const getFriendshipButton = () => {
    if (!user || !user.friendshipStatus) return null;

    switch (user.friendshipStatus) {
      case 'none':
        return (
          <button
            onClick={() => handleFriendAction('send')}
            className="flex items-center justify-center space-x-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors w-full"
          >
            <UserPlus className="w-4 h-4" />
            <span>Add Friend</span>
          </button>
        );
      case 'request_sent':
        return (
          <button
            onClick={() => handleFriendAction('cancel')}
            className="flex items-center justify-center space-x-2 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors w-full"
          >
            <Clock className="w-4 h-4" />
            <span>Cancel Request</span>
          </button>
        );
      case 'request_received':
        return (
          <div className="flex flex-col space-y-2">
            <button
              onClick={() => handleFriendAction('accept')}
              className="flex items-center justify-center space-x-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors w-full"
            >
              <Check className="w-4 h-4" />
              <span>Accept</span>
            </button>
            <button
              onClick={() => handleFriendAction('decline')}
              className="flex items-center justify-center space-x-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors w-full"
            >
              <X className="w-4 h-4" />
              <span>Decline</span>
            </button>
          </div>
        );
      case 'friends':
        return (
          <div className="flex flex-col space-y-2">
            <button
              onClick={() => handleFriendAction('chat')}
              className="flex items-center justify-center space-x-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors w-full"
            >
              <MessageSquare className="w-4 h-4" />
              <span>Chat</span>
            </button>
            <Link
              to="/live-debate"
              className="flex items-center justify-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors w-full"
            >
              <Mic className="w-4 h-4" />
              <span>Challenge to Debate</span>
            </Link>
            <button
              onClick={() => handleFriendAction('remove')}
              className="flex items-center justify-center space-x-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors w-full"
            >
              <UserMinus className="w-4 h-4" />
              <span>Remove Friend</span>
            </button>
          </div>
        );
      case 'blocked':
        return (
          <button
            onClick={() => handleFriendAction('unblock')}
            className="flex items-center justify-center space-x-2 px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors w-full"
          >
            <ShieldOff className="w-4 h-4" />
            <span>Unblock</span>
          </button>
        );
      default:
        return null;
    }
  };

  const getBlockButton = () => {
    if (!user || user.friendshipStatus === 'blocked') return null;

    return (
      <button
        onClick={() => handleFriendAction('block')}
        className="flex items-center justify-center space-x-2 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors w-full"
      >
        <Shield className="w-4 h-4" />
        <span>Block User</span>
      </button>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-300">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">User Not Found</h2>
          <p className="text-gray-600 dark:text-gray-300 mb-4">The user you're looking for doesn't exist.</p>
          <Link
            to="/friends"
            className="bg-primary-500 text-white px-4 py-2 rounded-lg hover:bg-primary-600 transition-colors"
          >
            Back to Friends
          </Link>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', name: 'Overview', icon: Users },
    { id: 'achievements', name: 'Achievements', icon: Award },
    { id: 'debates', name: 'Debate History', icon: Mic },
    { id: 'training', name: 'Training Progress', icon: BookOpen }
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pt-24 pb-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {message && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 bg-blue-50 dark:bg-blue-900 border border-blue-200 dark:border-blue-700 rounded-lg"
          >
            <span className="text-blue-800 dark:text-blue-200">{message}</span>
          </motion.div>
        )}

        {/* Profile Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-6"
        >
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between">
            <div className="flex items-center space-x-4 mb-4 md:mb-0">
              <div className="w-20 h-20 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full flex items-center justify-center text-white font-bold text-2xl">
                {user.profileImage ? (
                  <img src={user.profileImage} alt={user.username} className="w-20 h-20 rounded-full object-cover" />
                ) : (
                  <span>{user.username.charAt(0).toUpperCase()}</span>
                )}
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                  {user.firstName && user.lastName 
                    ? `${user.firstName} ${user.lastName}` 
                    : user.username
                  }
                </h1>
                <p className="text-lg text-gray-500 dark:text-gray-400">@{user.username}</p>
                {user.bio && (
                  <p className="text-gray-600 dark:text-gray-300 mt-2">{user.bio}</p>
                )}
                <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500 dark:text-gray-400">
                  {user.location && (
                    <div className="flex items-center space-x-1">
                      <MapPin className="w-4 h-4" />
                      <span>{user.location}</span>
                    </div>
                  )}
                  <div className="flex items-center space-x-1">
                    <Calendar className="w-4 h-4" />
                    <span>Joined {formatDate(user.joinDate)}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-col space-y-2">
              {getFriendshipButton()}
              {getBlockButton()}
              {currentUser && currentUser.id !== user.id && (
                <button
                  onClick={() => handleFriendAction('report')}
                  className="flex items-center justify-center space-x-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors w-full"
                >
                  <Shield className="w-4 h-4" />
                  <span>Report User</span>
                </button>
              )}
            </div>
          </div>

          {/* Stats Overview */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
            <div className="text-center">
              <div className="flex items-center justify-center space-x-1 mb-1">
                <Zap className="w-5 h-5 text-yellow-500" />
                <span className="text-2xl font-bold text-gray-900 dark:text-white">{user.xp || 0}</span>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Total XP</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center space-x-1 mb-1">
                <Crown className="w-5 h-5 crown-icon" />
                <span className="text-2xl font-bold text-gray-900 dark:text-white">{user.crowns || 0}</span>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Crowns</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center space-x-1 mb-1">
                <Trophy className="w-5 h-5 trophy-icon" />
                <span className="text-2xl font-bold text-gray-900 dark:text-white">{user.trophies || 0}</span>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Trophies</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center space-x-1 mb-1">
                <TrendingUp className="w-5 h-5 text-green-500" />
                <span className="text-2xl font-bold text-gray-900 dark:text-white">{user.winRate || 0}%</span>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Win Rate</p>
            </div>
          </div>
        </motion.div>

        {/* Tabs */}
        <div className="mb-6">
          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="-mb-px flex space-x-8">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  <span>{tab.name}</span>
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6"
        >
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Debate Statistics</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-300">Total Debates</span>
                      <span className="font-medium text-gray-900 dark:text-white">{user.totalDebates || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-300">Debates Won</span>
                      <span className="font-medium text-green-600">{user.debatesWon || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-300">Debates Lost</span>
                      <span className="font-medium text-red-600">{user.debatesLost || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-300">Current Streak</span>
                      <span className="font-medium text-orange-600 flex items-center">
                        <span className="text-[10px] leading-none mr-1">🔥</span>
                        {user.streak || 0} days
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-300">Level</span>
                      <span className="font-medium text-gray-900 dark:text-white">{user.level || 1}</span>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Social Links</h3>
                  <div className="space-y-3">
                    {user.website && (
                      <a
                        href={user.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center space-x-2 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                      >
                        <Globe className="w-4 h-4" />
                        <span>Website</span>
                      </a>
                    )}
                    {user.twitter && (
                      <a
                        href={`https://twitter.com/${user.twitter}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center space-x-2 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                      >
                        <Twitter className="w-4 h-4" />
                        <span>Twitter</span>
                      </a>
                    )}
                    {user.linkedin && (
                      <a
                        href={user.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center space-x-2 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                      >
                        <Linkedin className="w-4 h-4" />
                        <span>LinkedIn</span>
                      </a>
                    )}
                    {!user.website && !user.twitter && !user.linkedin && (
                      <p className="text-gray-500 dark:text-gray-400">No social links added</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'achievements' && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Achievements</h3>
              {user.achievements && user.achievements.length > 0 ? (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {user.achievements.map((achievement, index) => (
                    <div
                      key={index}
                      className="bg-gradient-to-r from-yellow-400 to-orange-500 p-4 rounded-lg text-white"
                    >
                      <div className="flex items-center space-x-2 mb-2">
                        <Award className="w-5 h-5" />
                        <h4 className="font-semibold">{achievement.name || `Achievement ${index + 1}`}</h4>
                      </div>
                      <p className="text-sm opacity-90">
                        {achievement.description || 'Achievement unlocked!'}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Award className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 dark:text-gray-300">No achievements yet</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'debates' && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Recent Debate History</h3>
              {user.debateHistory && user.debateHistory.length > 0 ? (
                <div className="space-y-4">
                  {user.debateHistory.slice(0, 10).map((debate, index) => (
                    <div
                      key={index}
                      className="border border-gray-200 dark:border-gray-700 rounded-lg p-4"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium text-gray-900 dark:text-white">
                            {debate.topic || `Debate ${index + 1}`}
                          </h4>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {debate.date ? formatDate(debate.date) : 'Date not available'}
                          </p>
                        </div>
                        <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                          debate.result === 'won' 
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                            : debate.result === 'lost'
                            ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                            : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
                        }`}>
                          {debate.result || 'Unknown'}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Mic className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 dark:text-gray-300">No debate history available</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'training' && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Training Progress</h3>
              {user.completedModules && user.completedModules.length > 0 ? (
                <div className="space-y-4">
                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white mb-2">Completed Modules</h4>
                      <div className="space-y-2">
                        {user.completedModules.map((module, index) => (
                          <div
                            key={index}
                            className="flex items-center space-x-2 p-2 bg-green-50 dark:bg-green-900 rounded"
                          >
                            <Check className="w-4 h-4 text-green-600 dark:text-green-400" />
                            <span className="text-sm text-gray-700 dark:text-gray-300">
                              {module.name || `Module ${index + 1}`}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white mb-2">Progress</h4>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600 dark:text-gray-300">Total Time Spent</span>
                          <span className="text-gray-900 dark:text-white">
                            {Math.round((user.totalTimeSpent || 0) / 60)} minutes
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600 dark:text-gray-300">Modules Completed</span>
                          <span className="text-gray-900 dark:text-white">{user.completedModules.length}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 dark:text-gray-300">No training progress available</p>
                </div>
              )}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default UserProfile; 